import { set, flow, isUndefined, isNull, unset } from 'lodash/fp';
import { Reducer } from 'redux';

import { LoginActionTypes } from '../../../auth/login/store/types';
import { newCalculatorField } from '../../../shared/calculator/constants';
import { CalculatorPromptDB, CalculatorPromptUI } from '../../calculator/store/types';
import { ClientCalculatorActionTypes, ClientCalculatorState } from './types';

export const initialCalculatorPrompt = (): CalculatorPromptUI => ({
    calculatorFieldType: null,
    calculation: [newCalculatorField(null)],
    calculationText: '',
    priorityOrder: 1,
    isSystem: false
});

const stripCalculatorPrompt = (calculatorPrompt: CalculatorPromptDB): CalculatorPromptUI => flow(
    unset('createdBy'),
    unset('createdByUsername'),
    unset('createdDate'),
    unset('clientId')
)(calculatorPrompt);

const duplicateCalculatorPrompt = (calculatorPrompt: CalculatorPromptDB, priorityOrder: number): CalculatorPromptUI => flow(
    unset('promptId'),
    unset('createdBy'),
    unset('createdByUsername'),
    unset('createdDate'),
    unset('clientId'),
    set('priorityOrder', priorityOrder),
    set('isSystem', false)
)(calculatorPrompt);

export const INITIAL_STATE: ClientCalculatorState = {
    isFetchingCalculatorPrompts: false,
    isSavingCalculatorPrompt: false,
    isDeletingCalculatorPrompt: false,
    calculatorPrompts: [],
    selectedCalculatorPrompt: null,
    calculatorPromptToDelete: null,
    selectedPromptUpdated: false,
    showCalculatorPreview: false,
    currentSelectedFieldTypePromptOrder: [],
    savedSelectedFieldTypePromptOrder: []
};

export const clientCalculatorReducer: Reducer<ClientCalculatorState> = (state = INITIAL_STATE, { type, payload }): ClientCalculatorState => {
    switch (type) {
        case ClientCalculatorActionTypes.FETCH_CLIENT_CALCULATOR_PROMPTS_STARTED:
            return set('isFetchingCalculatorPrompts', true, state);
        case ClientCalculatorActionTypes.FETCH_CLIENT_CALCULATOR_PROMPTS_SUCCESSFUL:
            return flow(
                set('isFetchingCalculatorPrompts', false),
                set('calculatorPrompts', payload)
            )(state);
        case ClientCalculatorActionTypes.FETCH_CLIENT_CALCULATOR_PROMPTS_FAILED:
            return set('isFetchingCalculatorPrompts', false, state);
        case ClientCalculatorActionTypes.UPSERT_CLIENT_CALCULATOR_PROMPT_STARTED:
            return set('isSavingCalculatorPrompt', true, state);
        case ClientCalculatorActionTypes.UPSERT_CLIENT_CALCULATOR_PROMPT_SUCCESSFUL:
            return flow(
                set('isSavingCalculatorPrompt', false),
                set('selectedCalculatorPrompt', null),
                set('calculatorPrompts', payload)
            )(state);
        case ClientCalculatorActionTypes.UPSERT_CLIENT_CALCULATOR_PROMPT_FAILED:
            return set('isSavingCalculatorPrompt', false, state);
        case ClientCalculatorActionTypes.DELETE_CLIENT_CALCULATOR_PROMPT_STARTED:
            return set('isDeletingCalculatorPrompt', true, state);
        case ClientCalculatorActionTypes.DELETE_CLIENT_CALCULATOR_PROMPT_SUCCESSFUL:
            return flow(
                set('isDeletingCalculatorPrompt', false),
                set('selectedCalculatorPrompt', null),
                set('calculatorPromptToDelete', null),
                set('calculatorPrompts', payload)
            )(state);
        case ClientCalculatorActionTypes.DELETE_CLIENT_CALCULATOR_PROMPT_FAILED:
            return set('isDeletingCalculatorPrompt', false, state);
        case ClientCalculatorActionTypes.SELECT_CLIENT_CALCULATOR_PROMPT: {
            let selectedCalculatorPrompt = initialCalculatorPrompt();
            if (isNull(payload)) {
                return set('selectedCalculatorPrompt', null, state);
            }
            if (!isUndefined(payload)) {
                selectedCalculatorPrompt = stripCalculatorPrompt(state.calculatorPrompts.find(({ promptId }) => promptId === payload)!);
            }
            return set('selectedCalculatorPrompt', selectedCalculatorPrompt, state);
        }
        case ClientCalculatorActionTypes.TOGGLE_DELETE_CLIENT_CALCULATOR_PROMPT_CONFIRMATION_MODAL: {
            const promptToDelete = !isNull(payload) ? state.calculatorPrompts.find(({ promptId }) => promptId === payload)! : null;
            return set('calculatorPromptToDelete', promptToDelete, state);
        }
        case ClientCalculatorActionTypes.UPDATE_SELECTED_CLIENT_CALCULATOR_PROMPT:
            return set('selectedCalculatorPrompt', payload, state);
        case ClientCalculatorActionTypes.SET_SELECTED_CLIENT_CALCULATOR_PROMPT_UPDATED:
            return set('selectedPromptUpdated', payload, state);
        case ClientCalculatorActionTypes.SET_CLIENT_CALCULATOR_PROMPT_ORDER:
            return flow(
                set('currentSelectedFieldTypePromptOrder', payload),
                set('savedSelectedFieldTypePromptOrder', payload)
            )(state);
        case ClientCalculatorActionTypes.UPDATE_CLIENT_CALCULATOR_PROMPT_ORDER:
            return set('currentSelectedFieldTypePromptOrder', payload, state);
        case ClientCalculatorActionTypes.SAVE_CLIENT_PROMPT_ORDER_STARTED:
            return set('isFetchingCalculatorPrompts', true, state);
        case ClientCalculatorActionTypes.SAVE_CLIENT_PROMPT_ORDER_SUCCESSFUL:
            return flow(
                set('isFetchingCalculatorPrompts', false),
                set('calculatorPrompts', payload)
            )(state);
        case ClientCalculatorActionTypes.SAVE_CLIENT_PROMPT_ORDER_FAILED:
            return set('isFetchingCalculatorPrompts', false, state);
        case ClientCalculatorActionTypes.DUPLICATE_SYSTEM_PROMPT: {
            const priorityOrder = state.calculatorPrompts.filter(({ isSystem }) => !isSystem).filter(({ calculatorFieldType }) => calculatorFieldType === payload.calculatorFieldType).length + 1;
            const selectedCalculatorPrompt = duplicateCalculatorPrompt(payload, priorityOrder);
            return set('selectedCalculatorPrompt', selectedCalculatorPrompt, state);
        }
        case LoginActionTypes.LOGOUT_SUCCESSFUL:
            return INITIAL_STATE;
        default:
            return state;
    }
};
